/**
 * @fileoverview Event handlers for the Reports page.
 * @module pages/reports/useReportsHandlers
 */
import { useCallback } from "react";
import dayjs from "dayjs";
import { loadFiltersForReportOption } from "../../logic/reports/reportOptionFilters.js";
import { generateReportOnScreenForOption } from "../../logic/reports/reportOnScreenLoaders.js";
import { loadEventsForReportYear } from "../../logic/reports/reportYearSelection.js";
import { notifyReportExportError } from "./reportExportUtils.js";
import { handleReportEventSelection } from "./reportEventSelection.js";
import { downloadReportByOption } from "./reportDownloadDispatch.js";
import { handleReportDateChange } from "./reportDateChange.js";

/** Returns memoized Reports page handlers. */
const useReportsHandlers = (ctx) => {
  const notifyExportError = useCallback(
    (error, fallbackType) =>
      notifyReportExportError(error, fallbackType, ctx.toastId),
    [ctx.toastId],
  );

  const resetReportFilters = useCallback(() => {
    ctx.setSelectedEvent(null);
    ctx.setSelectedMemberCompany(null);
    ctx.setStartDate(null);
    ctx.setEndDate(null);
    ctx.setSelectedBuyerCompanies([]);
    ctx.setSelectedYear(null);
    ctx.setSelectedEventTypes([]);
    ctx.setSelectedB2bEvent(null);
    ctx.setB2bEventsOptions([]);
  }, [ctx]);

  const generateReportOnScreen = useCallback(async () => {
    await generateReportOnScreenForOption(ctx.selectedReportOption?.value, {
      selectedEvent: ctx.selectedEvent,
      selectedBuyerCompanies: ctx.selectedBuyerCompanies,
      navigate: ctx.navigate,
      setSalaLayouts: ctx.setSalaLayouts,
      setSelectedEvent: ctx.setSelectedEvent,
      setLoadingLayout: ctx.setLoadingLayout,
      setBuyerCompaniesData: ctx.setBuyerCompaniesData,
      notifyExportError,
    });
  }, [ctx, notifyExportError]);

  const onSelectReportOption = useCallback(
    async (option) => {
      ctx.setSelectedReportOption(option);

      if (ctx.selectedReportOption?.value !== option.value) {
        resetReportFilters();
      }

      await loadFiltersForReportOption(option.value, {
        navigate: ctx.navigate,
        memberCompaniesOptions: ctx.memberCompaniesOptions,
        eventsOptions: ctx.eventsOptions,
        allBuyerCompanies: ctx.allBuyerCompanies,
        b2bEventsOptions: ctx.b2bEventsOptions,
        allEventsOptions: ctx.allEventsOptions,
        setEventsOptions: ctx.setEventsOptions,
        setLoadingEvents: ctx.setLoadingEvents,
        setMemberCompaniesOptions: ctx.setMemberCompaniesOptions,
        setLoadingMemberCompanies: ctx.setLoadingMemberCompanies,
        setAllBuyerCompanies: ctx.setAllBuyerCompanies,
        setBuyerCompaniesOptions: ctx.setBuyerCompaniesOptions,
        setLoadingBuyerCompanies: ctx.setLoadingBuyerCompanies,
        setB2bEventsOptions: ctx.setB2bEventsOptions,
        setLoadingB2bEvents: ctx.setLoadingB2bEvents,
        setSelectedEvent: ctx.setSelectedEvent,
        setSelectedB2bEvent: ctx.setSelectedB2bEvent,
        setAllEventsOptions: ctx.setAllEventsOptions,
      });
    },
    [ctx, resetReportFilters],
  );

  const onSelectYear = useCallback(
    async (year) => {
      ctx.setSelectedYear(year);
      ctx.setSelectedEvent(null);

      await loadEventsForReportYear(ctx.selectedReportOption?.value, year, {
        navigate: ctx.navigate,
        setEventsOptions: ctx.setEventsOptions,
        setLoadingEvents: ctx.setLoadingEvents,
        setB2bEventsOptions: ctx.setB2bEventsOptions,
        setLoadingB2bEvents: ctx.setLoadingB2bEvents,
        setSelectedB2bEvent: ctx.setSelectedB2bEvent,
      });
    },
    [ctx],
  );

  const onSelectEvent = useCallback(
    async (event) => {
      await handleReportEventSelection(event, ctx);
    },
    [ctx],
  );

  const onSelectB2bBuyerCompanies = useCallback(
    async (companies) => {
      ctx.setSelectedB2bBuyerCompanies(companies);
      if (companies?.length > 0) {
        const b2bBuyerUsers = companies.flatMap(
          (company) => company.customerCompanyUsers,
        );
        ctx.setB2bBuyerUsersOptions(
          b2bBuyerUsers.map((user) => ({
            ...user,
            search: user.nombre.toLowerCase(),
          })),
        );
        return;
      }
      ctx.setB2bBuyerUsersOptions([]);
    },
    [ctx],
  );

  const handleDateChange = useCallback(
    (date, dateType) => {
      handleReportDateChange(date, dateType, ctx);
    },
    [ctx],
  );

  const downloadData = useCallback(async () => {
    await downloadReportByOption(ctx.selectedReportOption?.value, ctx);
  }, [ctx]);

  const onStartDateChange = useCallback(
    (date) => handleDateChange(date, "startDate"),
    [handleDateChange],
  );

  const onEndDateChange = useCallback(
    (date) => handleDateChange(date, "endDate"),
    [handleDateChange],
  );

  const onYearOptionChange = useCallback(
    (option) => onSelectYear(option?.value ?? null),
    [onSelectYear],
  );

  const onEventTypesChange = useCallback(
    (options) => ctx.setSelectedEventTypes(options ?? []),
    [ctx.setSelectedEventTypes],
  );

  const onToggleBilled = useCallback(
    () => ctx.setBilled((previous) => !previous),
    [ctx.setBilled],
  );

  const getDisabledStartDate = useCallback(
    (current) => {
      if (!current) {
        return false;
      }
      const today = dayjs().endOf("day");
      const maxDate = ctx.endDate ? dayjs(ctx.endDate) : today;
      return current > maxDate;
    },
    [ctx.endDate],
  );

  const getDisabledEndDate = useCallback(
    (current) => {
      if (!current) {
        return false;
      }
      const today = dayjs().endOf("day");
      const minDate = ctx.startDate ? dayjs(ctx.startDate) : null;
      return current > today || (minDate && current < minDate);
    },
    [ctx.startDate],
  );

  return {
    onSelectReportOption,
    onSelectYear,
    onSelectEvent,
    onSelectB2bBuyerCompanies,
    handleDateChange,
    onStartDateChange,
    onEndDateChange,
    onYearOptionChange,
    onEventTypesChange,
    onToggleBilled,
    generateReportOnScreen,
    downloadData,
    getDisabledStartDate,
    getDisabledEndDate,
  };
};

export default useReportsHandlers;
